import type { Transform2D } from '@cinegrid/core';
import { containScale } from '../../engine/aspect';

export type Vec2 = { x: number; y: number };

/** Match VERT_SHADER: scale around anchor, aspect-correct rotate, then offset. */
export function transformPoint(
  pos: Vec2,
  t: Transform2D,
  fit: { scaleX: number; scaleY: number },
  aspect: number
): Vec2 {
  let px = (pos.x - t.anchorX) * (t.scaleX * fit.scaleX);
  let py = (pos.y - t.anchorY) * (t.scaleY * fit.scaleY);
  const c = Math.cos((t.rotation * Math.PI) / 180);
  const s = Math.sin((t.rotation * Math.PI) / 180);
  const a = Math.max(aspect, 1e-6);
  px *= a;
  const rx = c * px - s * py;
  const ry = s * px + c * py;
  return {
    x: rx / a + t.positionX + t.anchorX,
    y: ry + t.positionY + t.anchorY,
  };
}

export function layerFitScale(
  sourceSize: { width: number; height: number } | null | undefined,
  frameW: number,
  frameH: number
): { scaleX: number; scaleY: number } {
  if (!sourceSize?.width || !sourceSize?.height) return { scaleX: 1, scaleY: 1 };
  return containScale(sourceSize.width, sourceSize.height, frameW, frameH);
}

/** Unit-quad corners in frame UV after transform (TL, TR, BR, BL). */
export function layerCorners(
  t: Transform2D,
  fit: { scaleX: number; scaleY: number },
  aspect: number
): [Vec2, Vec2, Vec2, Vec2] {
  return [
    transformPoint({ x: 0, y: 0 }, t, fit, aspect),
    transformPoint({ x: 1, y: 0 }, t, fit, aspect),
    transformPoint({ x: 1, y: 1 }, t, fit, aspect),
    transformPoint({ x: 0, y: 1 }, t, fit, aspect),
  ];
}

export function layerAnchor(
  t: Transform2D,
  fit: { scaleX: number; scaleY: number },
  aspect: number
): Vec2 {
  return transformPoint({ x: t.anchorX, y: t.anchorY }, t, fit, aspect);
}

/** Top-edge midpoint in frame UV (for rotate handle base). */
export function layerTopMid(
  t: Transform2D,
  fit: { scaleX: number; scaleY: number },
  aspect: number
): Vec2 {
  return transformPoint({ x: 0.5, y: 0 }, t, fit, aspect);
}

/** Aspect-corrected angle (degrees) from anchor to point in frame UV. */
export function aspectAngleDeg(from: Vec2, to: Vec2, aspect: number): number {
  const dx = (to.x - from.x) * Math.max(aspect, 1e-6);
  const dy = to.y - from.y;
  return (Math.atan2(dy, dx) * 180) / Math.PI;
}

/** Distance in aspect-corrected frame UV (circular metric). */
export function aspectDist(a: Vec2, b: Vec2, aspect: number): number {
  const dx = (b.x - a.x) * Math.max(aspect, 1e-6);
  const dy = b.y - a.y;
  return Math.hypot(dx, dy);
}

export function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

export function clientToFrameUv(
  clientX: number,
  clientY: number,
  rect: DOMRect
): Vec2 | null {
  if (rect.width <= 0 || rect.height <= 0) return null;
  return {
    x: (clientX - rect.left) / rect.width,
    y: (clientY - rect.top) / rect.height,
  };
}
